/**
 * Application state controller hook
 * Handles authentication state and user sessions
 */
import { useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import { AppStateAtom, ClientStateAtom, FundAtom } from "./store";
import { checkToken } from "./api";
import { Fund } from "./types/Fund";

/**
 * Interface for the useAppController hook return values
 */
interface UseAppController {
  /** Whether the app has completed initialization */
  initialised: boolean;
  /** Whether the current user has admin privileges */
  isUserAdmin: boolean;
}

/**
 * Hook that manages application initialization and authentication
 * Checks for user token and handles login/logout state
 */
export const useAppController = (): UseAppController => {
  const initializedRef = useRef<boolean>(false);
  const [appState, setAppState] = useAtom(AppStateAtom);
  const setClientData = useSetAtom(ClientStateAtom);
  const [fundState, setFundState] = useAtom(FundAtom);

  /**
   * Verifies the user's authentication token and updates app state accordingly
   */
  const performCheckToken = async () => {
    try {
      setClientData({
        clientId: 0,
        clientKey: "",
      });

      // Reset authentication state
      setAppState((prevState) => ({
        ...prevState,
        loading: true,
        errorMessage: "",
        authed: false,
        user: null,
        subscriptionActive: false,
      }));

      // Reset fund state
      setFundState((prevState) => ({
        ...prevState,
        funds: [],
        selectedFund: null,
      }));

      // If no token exists, set app as initialized but not authenticated
      if (localStorage.getItem("token") === null) {
        setAppState((prevState) => ({
          ...prevState,
          authed: false,
          loading: false,
          initialised: true,
          user: null,
          subscriptionActive: false,
          role: "user",
          featureFlags: [],
        }));
        return;
      }

      // Verify token with server
      const res = await checkToken();

      if (res?.success) {
        // Authentication successful
        setAppState((prevState) => ({
          ...prevState,
          authed: true,
          loading: false,
          initialised: true,
          email: res.user.email,
          user: res.user,
          role: res.user.role,
          subscriptionActive: res.user.subscriptionStatus,
          gridTheme: res.user.gridTheme || "ag-theme-alpine",
          featureFlags: res.user.featureFlags,
        }));

        setClientData({
          clientId: parseInt(res.user.clientId),
          clientKey: res.user.clientKey,
        });
        localStorage.setItem("clientId", res.user.clientId);
        localStorage.setItem("clientKey", res.user.clientKey);

        localStorage.removeItem("selectedFund");

        // Update fund state with first fund as default selection
        setFundState((prevState) => ({
          ...prevState,
          funds: res.funds,
          selectedFund: res.funds[0],
        }));

        // Redirect to dashboard if on login page
        if (window.location.pathname === "/app/login") {
          window.location.href = "/app/dashboard";
        }
      } else {
        // Authentication failed, redirect to login if needed
        if (window.location.pathname !== "/app/login") {
          window.location.href = "../app/login";
        }

        // Reset app state
        setAppState((prevState) => ({
          ...prevState,
          authed: false,
          loading: false,
          initialised: true,
          user: null,
          subscriptionActive: false,
          gridTheme: "ag-theme-alpine",
        }));

        // Reset fund state
        setFundState((prevState) => ({
          ...prevState,
          funds: [],
          selectedFund: null,
        }));
      }
    } catch (error) {
      // Handle any errors during authentication
      setAppState((prevState) => ({
        ...prevState,
        authed: false,
        loading: false,
        initialised: true,
        subscriptionActive: false,
        gridTheme: "ag-theme-alpine",
      }));
    }
  };

  // Initialize the app on first render
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      performCheckToken();
    }
  }, []);

  return {
    initialised: appState.initialised || false,
    isUserAdmin: appState.user?.role === "admin",
  };
};
